// Koneksi. Concept lines (JEV ui.region_gate, see the build report):
//   conn_header  Job: name the screen and what it controls. Message: Koneksi,
//                the bot's own WhatsApp number linked as a device. Action:
//                none. Container: plain spacing.
//   conn_status  Job: tell at a glance whether the bot can receive messages.
//                Message: status in words, transport, linked number, last
//                error in plain Indonesian, update time. Action: none.
//                Container: a hairline spec list (label lane, value).
//   conn_link    Job: link the bot number. Message: one phone field asks
//                WhatsApp for an 8-character code; the code is read out in
//                mono "ABCD-EFGH" beside four numbered steps; it expires in a
//                few minutes. Action: Minta kode. Container: card for the
//                form, the Kode readout pattern for the code (8px rise).
//   conn_open    Job: confirm the link and allow unlinking. Message:
//                Terhubung sebagai +62... Action: Logout, behind the
//                AlertDialog. Container: card.
// Polls every 3 s while the status is connecting or pairing and the tab is
// visible, else every 10 s (docs/api/dashboard-contract.md, Addendum
// 2026-10-14). Hand-written inside the kit Section: the kit has no form,
// spec list for a device state, or readout composition.
import { useEffect, useId, useState, type FormEvent } from "react";
import { Section } from "@bot-whatsapp/ui";
import { ApiError, isUnauthorized, useDashboardApi } from "./api";
import { useOnUnauthorized, useResource } from "./data";
import { formatDateTime } from "./format";
import type { Connection, ConnectionStatus } from "./types";
import { ConfirmDialog, CopyCodeButton, LoadError, Notice, ScreenHeader, StatusBadge, type ConfirmRequest } from "./ui";

export const FAST_POLL_MS = 3_000;
export const SLOW_POLL_MS = 10_000;

const STATUS: Record<ConnectionStatus, { label: string; tone: "neutral" | "success" | "info" | "danger" }> = {
  disconnected: { label: "Terputus", tone: "neutral" },
  connecting: { label: "Menghubungkan", tone: "info" },
  pairing: { label: "Menunggu kode", tone: "info" },
  open: { label: "Terhubung", tone: "success" },
  logged_out: { label: "Keluar", tone: "neutral" },
};

const LAST_ERROR: Record<string, string> = {
  pairing_expired: "Kode kedaluwarsa sebelum dimasukkan. Minta kode baru.",
  pairing_unavailable: "WhatsApp belum siap saat kode diminta. Coba lagi sebentar.",
  pairing_failed: "WhatsApp menolak permintaan kode. Coba lagi sebentar.",
  logged_out: "Perangkat bot dilepas dari WhatsApp. Tautkan lagi dengan kode baru.",
  connection_closed: "Sambungan ke WhatsApp terputus. Bot mencoba menyambung lagi.",
};

/** "K7QF2M9X" read as "K7QF-2M9X". */
export function formatLinkCode(code: string): string {
  return code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
}

function lastErrorText(code: string): string {
  return LAST_ERROR[code] ?? `Kesalahan terakhir dari WhatsApp: ${code}.`;
}

/** The field message of a 400, in Indonesian. */
function phoneIssue(err: ApiError): string {
  const message = err.issues.find((i) => i.path === "phone")?.message ?? err.issues[0]?.message ?? "";
  if (message.includes("10 to 15")) return "Nomor harus 10 sampai 15 digit.";
  if (message.includes("phone number")) return "Isi nomor dengan angka saja, boleh diawali + atau 08.";
  return message ? `Nomor ditolak server: ${message}.` : "Nomor ditolak server. Periksa lagi nomornya.";
}

function requestFailure(err: unknown): { field?: string; notice?: { title: string; body: string } } {
  if (!(err instanceof ApiError)) return { notice: { title: "Kode gagal diminta", body: "Coba lagi sebentar." } };
  switch (err.status) {
    case 400:
      if (err.message === "not_bot_phone") {
        return {
          notice: { title: "Nomor ditolak", body: "Nomor ini bukan nomor bot yang tertaut. Logout dulu untuk mengganti nomor." },
        };
      }
      return { field: phoneIssue(err) };
    case 409:
      return { notice: { title: "Nomor bot sudah tertaut", body: "Logout dulu jika ingin menautkan nomor lain." } };
    case 422:
      return {
        notice: { title: "Penautan tidak dipakai", body: "Bot memakai Cloud API, jadi nomor tidak ditautkan dengan kode." },
      };
    case 429:
      return { notice: { title: "Terlalu banyak permintaan, coba lagi nanti", body: "Batasnya 5 permintaan kode per 10 menit." } };
    case 503:
      if (err.message === "rate_limit_unavailable") {
        return {
          notice: {
            title: "Batas permintaan sedang tidak tersedia, coba lagi sebentar",
            body: "Kode belum diminta karena penghitung batas belum bisa dipakai.",
          },
        };
      }
      return { notice: { title: "WhatsApp belum siap, coba lagi sebentar", body: "Jendela penautan belum terbuka dalam 20 detik." } };
    case 0:
      return { notice: { title: "Server tidak dapat dihubungi", body: "Pastikan server bot berjalan, lalu coba lagi." } };
    default:
      return { notice: { title: "Kode gagal diminta", body: "Coba lagi sebentar." } };
  }
}

/** Time since an instant as a mono "mm.ss" that ticks each second. The
 *  backend sets no expiry of its own (WhatsApp closes the link window and
 *  the status reports pairing_expired), so the screen shows how long the
 *  code has been out, never an invented countdown. */
function Elapsed({ since }: { since: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const seconds = Math.max(0, Math.floor((now - Date.parse(since)) / 1000));
  const minutes = Math.floor(seconds / 60);
  // Words, not "00.02": a clock-like readout was misread as a time of day.
  const text = minutes === 0 ? `${seconds} detik` : `${minutes} menit ${seconds % 60} detik`;
  return <span className="kit-num dash-elapsed">{text}</span>;
}

/** True while the page is visible (SSR and tests count as visible). */
function useVisible(): boolean {
  const read = () => typeof document === "undefined" || document.visibilityState !== "hidden";
  const [visible, setVisible] = useState(read);
  useEffect(() => {
    if (typeof document === "undefined") return;
    const on = () => setVisible(read());
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);
  return visible;
}

export function ConnectionScreen() {
  const api = useDashboardApi();
  const onUnauthorized = useOnUnauthorized();
  const headingId = useId();
  const statusTitleId = useId();
  const formTitleId = useId();
  const codeTitleId = useId();
  const openTitleId = useId();
  const phoneId = useId();
  const phoneHintId = useId();

  const visible = useVisible();
  const [busyStatus, setBusyStatus] = useState(false);
  const conn = useResource(() => api.connection(), [api], {
    pollMs: busyStatus && visible ? FAST_POLL_MS : SLOW_POLL_MS,
    pollHidden: true,
  });
  const [override, setOverride] = useState<Connection | null>(null);
  const c = override && (!conn.data || Date.parse(override.updatedAt) >= Date.parse(conn.data.updatedAt)) ? override : conn.data;

  useEffect(() => {
    setBusyStatus(c?.status === "connecting" || c?.status === "pairing");
  }, [c?.status]);

  const [phone, setPhone] = useState("");
  // The bot number the server locks pairing to (WA_BOT_PHONE or the linked
  // device): shown as text, never as a free input, and the only number asked for.
  const lockedPhone = c?.botPhone && c.botPhoneSource ? c.botPhone : null;
  const botPhone = c?.botPhone ?? null;
  useEffect(() => {
    if (botPhone) setPhone((prev) => prev || botPhone);
  }, [botPhone]);
  const [requesting, setRequesting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ title: string; body: string } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  async function request(number: string) {
    if (requesting) return;
    const clean = (lockedPhone ?? number).trim();
    if (!clean) {
      setFieldError("Isi nomor WhatsApp bot, misalnya 081234567890.");
      return;
    }
    setRequesting(true);
    setFieldError(null);
    setFailure(null);
    try {
      setOverride(await api.requestPairingCode(clean));
      conn.reload();
    } catch (err) {
      if (isUnauthorized(err)) onUnauthorized();
      const f = requestFailure(err);
      setFieldError(f.field ?? null);
      setFailure(f.notice ?? null);
    } finally {
      setRequesting(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    void request(phone);
  }

  function askLogout(current: Connection) {
    setConfirmError(null);
    setConfirm({
      title: "Logout nomor bot?",
      body: `Perangkat bot dilepas dari ${current.phone ?? current.botPhone ?? "nomor ini"}. Bot berhenti menerima pesan sampai nomor ditautkan lagi dengan kode baru.`,
      confirmLabel: "Logout",
      run: async () => {
        setOverride(await api.logoutConnection());
      },
    });
  }

  async function runConfirm() {
    if (!confirm || confirmBusy) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      await confirm.run();
      setConfirm(null);
      conn.reload();
    } catch (err) {
      if (isUnauthorized(err)) onUnauthorized();
      setConfirmError(
        err instanceof ApiError && err.status === 422
          ? "Bot memakai Cloud API, jadi tidak ada perangkat yang bisa dilepas."
          : "Logout belum berhasil. Coba lagi sebentar lagi.",
      );
    } finally {
      setConfirmBusy(false);
    }
  }

  const meta = c
    ? c.status === "open"
      ? "Nomor bot tertaut ke WhatsApp. Bot menerima pesan dari pengirim yang terpasang."
      : "Tautkan nomor WhatsApp bot sebagai perangkat dengan kode 8 karakter dari WhatsApp."
    : conn.error
      ? "Status koneksi belum dapat dimuat."
      : "Memuat status koneksi.";

  const s = c ? STATUS[c.status] : null;
  const cloud = c?.transport === "cloud";
  const showForm = c && !cloud && (c.status === "disconnected" || c.status === "connecting" || c.status === "logged_out");
  const showCode = c && !cloud && c.status === "pairing" && c.pairingCode;

  return (
    <Section id="koneksi" className="dash-screen dash-split-even" composition="custom" variant="device-link" labelledBy={headingId}>
      <ScreenHeader id={headingId} title="Koneksi" meta={meta} live />

      {conn.error && !c ? (
        <div className="kit-full">
          <LoadError what="Status koneksi" onRetry={conn.reload} />
        </div>
      ) : null}

      {c && s ? (
        <>
          <section className="dash-side" aria-labelledby={statusTitleId}>
            <div className="dash-region">
              <div className="dash-region-head">
                <h2 id={statusTitleId} className="kit-title">
                  Status bot
                </h2>
              </div>
              <dl className="dash-spec">
                <div>
                  <dt>Status</dt>
                  <dd>
                    <StatusBadge tone={s.tone}>{s.label}</StatusBadge>
                  </dd>
                </div>
                <div>
                  <dt>Transport</dt>
                  <dd>{cloud ? "Cloud API" : "Baileys, perangkat tertaut"}</dd>
                </div>
                <div>
                  <dt>Nomor bot</dt>
                  {c.status === "open" && c.phone ? <dd className="kit-num">{c.phone}</dd> : <dd>Belum tertaut</dd>}
                </div>
                <div>
                  <dt>Kesalahan</dt>
                  <dd>{c.lastError ? lastErrorText(c.lastError) : "Tidak ada"}</dd>
                </div>
                <div>
                  <dt>Diperbarui</dt>
                  <dd className="dash-time">{formatDateTime(c.updatedAt)}</dd>
                </div>
              </dl>
            </div>
          </section>

          <div className="dash-main">
            {cloud ? (
              <Notice tone="info" title="Penautan tidak dipakai">
                Bot memakai Cloud API dari Meta, jadi nomor bot tidak ditautkan dengan kode di sini.
              </Notice>
            ) : null}

            {showForm ? (
              <section className="dash-card" aria-labelledby={formTitleId}>
                <form className="dash-form" onSubmit={submit} noValidate>
                  <h2 id={formTitleId} className="kit-title">
                    Tautkan nomor bot
                  </h2>
                  {failure ? (
                    <Notice tone="danger" title={failure.title}>
                      {failure.body}
                    </Notice>
                  ) : null}
                  {lockedPhone ? (
                    <div className="field">
                      <p id={phoneId} className="kit-body">
                        Nomor bot terkunci: <span className="kit-num">{lockedPhone}</span>
                      </p>
                      <p id={phoneHintId} className="field-hint">
                        {c.botPhoneSource === "env"
                          ? "Nomor diatur server, jadi hanya nomor ini yang bisa ditautkan."
                          : "Nomor ini tertaut dari perangkat sebelumnya; logout membuka kunci agar bisa ganti nomor."}
                      </p>
                    </div>
                  ) : (
                  <div className="field">
                    <label htmlFor={phoneId}>Nomor WhatsApp bot</label>
                    <input
                      id={phoneId}
                      type="tel"
                      inputMode="tel"
                      autoComplete="off"
                      placeholder="081234567890"
                      value={phone}
                      maxLength={32}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        if (fieldError) setFieldError(null);
                      }}
                      aria-invalid={fieldError ? true : undefined}
                      aria-describedby={phoneHintId}
                      required
                    />
                    <p id={phoneHintId} className="field-hint" data-state={fieldError ? "error" : undefined}>
                      {fieldError ?? "Nomor yang dipakai bot, boleh diawali 08 atau +62."}
                    </p>
                  </div>
                  )}
                  <button type="submit" className="btn-primary btn-block" aria-busy={requesting || undefined}>
                    Minta kode
                  </button>
                  {c.botPhoneSource === "linked" ? (
                    // The remembered number survives a disconnect; this is the only way to
                    // release it when the device is not open (the open card has its own Logout).
                    <button type="button" className="btn-secondary btn-block dash-danger-quiet" onClick={() => askLogout(c)}>
                      Logout untuk ganti nomor
                    </button>
                  ) : null}
                </form>
              </section>
            ) : null}

            {showCode ? (
              <section key={c.pairingRequestedAt ?? c.pairingCode} className="dash-readout dash-link" aria-labelledby={codeTitleId}>
                <div className="dash-readout-head">
                  <h2 id={codeTitleId} className="dash-readout-title">
                    <span>Kode tautan untuk {c.phone}</span>
                  </h2>
                  <p className="kit-meta">
                    Diminta pukul <span className="dash-time">{formatDateTime(c.pairingRequestedAt ?? c.updatedAt)}</span>, berjalan{" "}
                    <Elapsed since={c.pairingRequestedAt ?? c.updatedAt} />. Kode berlaku sampai WhatsApp menutup jendela
                    penautan; jika lewat, status kembali Terputus dan kode baru bisa diminta.
                  </p>
                </div>
                <p className="dash-readout-code kit-num" aria-label={`Kode ${c.pairingCode!.split("").join(" ")}`}>
                  {formatLinkCode(c.pairingCode!)}
                </p>
                <div className="dash-readout-copy">
                  <CopyCodeButton value={c.pairingCode!} />
                </div>
                <div className="dash-link-again">
                  <button
                    type="button"
                    className="btn-ghost"
                    aria-busy={requesting || undefined}
                    onClick={() => void request(lockedPhone ?? c.phone ?? phone)}
                  >
                    Minta kode baru
                  </button>
                </div>
                {failure ? (
                  <div className="dash-link-note">
                    <Notice tone="danger" title={failure.title}>
                      {failure.body}
                    </Notice>
                  </div>
                ) : null}
                <ol className="dash-steps">
                  <li>Buka WhatsApp di HP nomor bot.</li>
                  <li>Buka Setelan, lalu Perangkat tertaut, lalu Tautkan perangkat.</li>
                  <li>Pilih "Tautkan dengan nomor telepon saja".</li>
                  <li>Masukkan kode ini.</li>
                </ol>
              </section>
            ) : null}

            {c.status === "open" && !cloud ? (
              <section className="dash-card dash-linked" aria-labelledby={openTitleId}>
                <div className="dash-linked-text">
                  <h2 id={openTitleId} className="kit-title">
                    Terhubung sebagai <span className="kit-num">{c.phone ?? "nomor bot"}</span>
                  </h2>
                  <p className="kit-body">Logout melepas perangkat bot dari WhatsApp. Setelah itu nomor bisa ditautkan lagi dengan kode baru.</p>
                </div>
                <button type="button" className="btn-secondary dash-danger-quiet" onClick={() => askLogout(c)}>
                  Logout
                </button>
              </section>
            ) : null}
          </div>
        </>
      ) : null}

      <ConfirmDialog
        request={confirm}
        busy={confirmBusy}
        error={confirmError}
        onConfirm={runConfirm}
        onClose={() => setConfirm(null)}
      />
    </Section>
  );
}
